import { Input, InputProps, InputRef } from 'antd';
import {
  ChangeEventHandler,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

export interface InputDecimalProps
  extends Omit<InputProps, 'onChange' | 'value'> {
  onChange?(value?: number): void;
  value?: number;
  fractions?: number;
  min?: number;
  max?: number;
  allowClear?: boolean;
}

enum EventInputType {
  insertText = 'insertText',
  deleteContentBackward = 'deleteContentBackward',
  deleteContentForward = 'deleteContentForward',
}

const MAX_VALUE = Number((9e13).toString().replace(/0/g, '9'));
const MIN_VALUE = -MAX_VALUE;

const InputDecimal = forwardRef<InputRef, InputDecimalProps>(function (
  {
    fractions = 2,
    min = MIN_VALUE,
    max = MAX_VALUE,
    value: propsValue,
    onChange,
    allowClear = true,
    ...props
  },
  ref,
) {
  const [value, setValue] = useState<number | undefined>(
    propsValue || allowClear ? undefined : 0,
  );
  const [inputSelections, setInputSelections] = useState<[number, number]>([
    0, 0,
  ]);
  const [isNegative, setIsNegative] = useState(false);

  useEffect(() => {
    setValue(propsValue);
  }, [propsValue]);

  const decimals = Math.pow(10, typeof fractions === 'number' ? fractions : 2);

  const inputRef = useRef<InputRef>(null);

  useImperativeHandle(ref, () => inputRef.current as InputRef);

  const inputValue = value?.toLocaleString('pt-BR', {
    minimumFractionDigits: fractions,
    maximumFractionDigits: fractions,
  });

  const stringToNumber = useCallback(
    (paramValue: string, negative = false) => {
      let valueNumber = paramValue
        ? Number(paramValue) / decimals
        : allowClear
        ? undefined
        : 0;

      if (typeof valueNumber === 'number' && negative) {
        valueNumber = Math.abs(valueNumber) * -1;
      }

      if (typeof valueNumber === 'number' && valueNumber <= min) {
        valueNumber = min;
        if (min >= 0 && negative) {
          setIsNegative(false);
        }
      } else if (typeof valueNumber === 'number' && valueNumber >= max) {
        valueNumber = max;
      }

      return valueNumber;
    },
    [allowClear, min, max],
  );

  const hasSymbol = (paramValue: string) => ['.', ','].includes(paramValue);

  const handleChange: ChangeEventHandler<HTMLInputElement> = useCallback(
    (e) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const inputType = (e.nativeEvent as any).inputType as EventInputType;
      const target = e.currentTarget;
      const targetValue = target.value;
      const targetValueLength = targetValue.length;

      const selectionStart = target.selectionStart || 0;
      const selectionEnd = target.selectionEnd || 0;

      // Pega o valor do input e retorna somente os números
      const valueString = targetValue.replace(/[^0-9]/g, '');
      const hasMinus = targetValue.includes('-');
      const hasPlus = targetValue.includes('+');

      setIsNegative((hasMinus || isNegative) && !hasPlus);

      // Transforma os números extraidos do input e converte para um valor numérico de 2 casas decimais
      // Zeros a esquerda são ignorados na transformação, se o campo estiver vazio retorna zero
      let valueParsed = stringToNumber(
        hasMinus && !valueString ? '0' : valueString,
        (hasMinus || isNegative) && !hasPlus,
      );

      if (hasMinus) {
        setValue(valueParsed);

        if (onChange) {
          onChange(valueParsed);
        }

        return;
      }

      if (typeof valueParsed === 'undefined') {
        setValue(valueParsed);
        setIsNegative(false);

        if (onChange) {
          onChange(valueParsed);
        }

        return;
      }

      // aplica mascara no valor transformado em número
      const valueWithLocale = valueParsed.toLocaleString('pt-BR', {
        minimumFractionDigits: fractions,
        maximumFractionDigits: fractions,
      });

      // Calcula a quantidade de caracteres no valor com mascara
      const valueLocaleLength = valueWithLocale.length;

      if (inputType === EventInputType.insertText) {
        setTimeout(() => {
          const isEqualSelection = inputSelections[0] === inputSelections[1];

          if (typeof inputValue !== 'number') {
            inputRef.current?.setSelectionRange(
              valueLocaleLength,
              valueLocaleLength,
              'backward',
            );
          } else if (isEqualSelection) {
            if (
              selectionStart > valueLocaleLength ||
              selectionStart === valueLocaleLength
            ) {
              // Ponteiro no final do input
              inputRef.current?.setSelectionRange(
                valueLocaleLength,
                valueLocaleLength,
                'backward',
              );
            } else if (
              valueLocaleLength - selectionStart === 1 &&
              valueWithLocale[1] === '.'
            ) {
              // Ponteiro no final do input
              inputRef.current?.setSelectionRange(
                valueLocaleLength,
                valueLocaleLength,
                'backward',
              );
            } else if (selectionStart === 1) {
              // Ponteiro no começo do input
              inputRef.current?.setSelectionRange(1, 1, 'forward');
            } else if (selectionStart > 1) {
              // Ponteiro no meio do input
              if (valueWithLocale[1] === '.') {
                inputRef.current?.setSelectionRange(
                  selectionStart + 1,
                  selectionEnd + 1,
                  'backward',
                );
              } else {
                inputRef.current?.setSelectionRange(
                  selectionStart,
                  selectionEnd,
                  'backward',
                );
              }
            }
          } else if (!isEqualSelection) {
            if (targetValueLength === 1) {
              inputRef.current?.setSelectionRange(
                valueLocaleLength,
                valueLocaleLength,
                'backward',
              );
            } else if (
              targetValue
                .slice(inputSelections[0], inputSelections[1])
                .includes(',')
            ) {
              inputRef.current?.setSelectionRange(
                inputSelections[1] + 1,
                inputSelections[1] + 1,
                'backward',
              );
            } else {
              inputRef.current?.setSelectionRange(
                inputSelections[1],
                inputSelections[1],
                'backward',
              );
            }
          }
        }, 0);
      } else if (
        inputType === EventInputType.deleteContentBackward ||
        inputType === EventInputType.deleteContentForward
      ) {
        if (
          inputType === EventInputType.deleteContentBackward &&
          value === 0 &&
          allowClear
        ) {
          setValue(undefined);

          if (onChange) {
            onChange(undefined);
          }

          return;
        }

        const isSymbol = inputValue && hasSymbol(inputValue[selectionStart]);

        if (isSymbol) {
          if (inputType === EventInputType.deleteContentBackward) {
            valueParsed = stringToNumber(
              valueString.slice(
                0,
                selectionStart === 0 ? 0 : selectionStart - 1,
              ) + valueString.slice(selectionEnd),
              hasMinus || isNegative,
            );
          } else {
            valueParsed = stringToNumber(
              valueString.slice(0, selectionStart) +
                valueString.slice(selectionEnd + 1),
              hasMinus || isNegative,
            );
          }
        }

        setTimeout(() => {
          if (!targetValue && selectionStart === 0) {
            // Campo vazio
            inputRef.current?.setSelectionRange(
              valueLocaleLength,
              valueLocaleLength,
              'backward',
            );
          } else if (selectionStart === 0) {
            // Ponteiro no inicio
            if (valueWithLocale[0].includes('0')) {
              inputRef.current?.setSelectionRange(1, 1, 'backward');
            } else {
              inputRef.current?.setSelectionRange(0, 0, 'backward');
            }
          } else if (valueWithLocale[0].includes('0')) {
            // Deletando com valor menor que 1,00
            if (inputType === EventInputType.deleteContentBackward) {
              inputRef.current?.setSelectionRange(
                valueLocaleLength,
                valueLocaleLength,
                'backward',
              );
            } else {
              if (valueWithLocale[selectionStart].includes(',')) {
                inputRef.current?.setSelectionRange(
                  selectionStart + 2,
                  selectionStart + 2,
                  'backward',
                );
              } else {
                inputRef.current?.setSelectionRange(
                  selectionStart + 1,
                  selectionStart + 1,
                  'backward',
                );
              }
            }
          } else {
            if (
              selectionStart === selectionEnd &&
              selectionStart !== valueLocaleLength
            ) {
              // Ponteiro no meio do valor
              inputRef.current?.setSelectionRange(
                isSymbol ? selectionStart - 1 : selectionStart,
                isSymbol ? selectionStart - 1 : selectionStart,
                inputType === EventInputType.deleteContentBackward
                  ? 'backward'
                  : 'forward',
              );
            } else {
              inputRef.current?.setSelectionRange(
                selectionStart,
                selectionStart,
                inputType === EventInputType.deleteContentBackward
                  ? 'backward'
                  : 'forward',
              );
            }
          }
        }, 0);
      }

      setValue(valueParsed);

      if (onChange) {
        onChange(valueParsed);
      }
    },
    [inputRef.current, inputValue, inputSelections, isNegative],
  );

  return (
    <Input
      {...props}
      value={inputValue}
      onChange={handleChange}
      ref={inputRef}
      inputMode="numeric"
      onSelect={(e) => {
        setInputSelections([
          e.currentTarget.selectionStart || 0,
          e.currentTarget.selectionEnd || 0,
        ]);
      }}
    />
  );
});

export default InputDecimal;
